
import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Button, TextField, Select, MenuItem,
    InputLabel, FormControl, Paper, Container, Snackbar, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton,
    Collapse, Card, CardContent, Divider, InputAdornment,  Autocomplete,Dialog,
    DialogTitle,
    DialogContent,
    CircularProgress,
    DialogContentText,
    DialogActions, Tooltip,
} from '@mui/material';
import { createTheme, ThemeProvider, styled } from '@mui/material/styles';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ClearIcon from '@mui/icons-material/Clear';
import PostAddIcon from '@mui/icons-material/PostAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import duration from 'dayjs/plugin/duration'; 
import MapboxClient from '@mapbox/mapbox-sdk/services/geocoding';
import axios from 'axios';

dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);

const theme = createTheme({
    palette: {
        primary: { main: '#11224d' },
        secondary: { main: '#0e1d3a' },
        warning: { main: '#ffb300' },
        background: { default: '#f9fafc', paper: '#ffffff' },
    },
    typography: { fontFamily: 'Inter, Arial, sans-serif' },
});

const formatRelativeTime = (timePosted) => {
    const diffInSec = dayjs().diff(dayjs(timePosted), 'second');
    if (diffInSec < 60) return `${diffInSec} sec`;
    const diffInMin = dayjs().diff(dayjs(timePosted), 'minute');
    if (diffInMin < 60) return `${diffInMin} min`;
    const diffInHr = dayjs().diff(dayjs(timePosted), 'hour');
    if (diffInHr < 24) return `${diffInHr} hr`;
    const diffInDays = dayjs().diff(dayjs(timePosted), 'day');
    return `${diffInDays} d`;
};

const getRegistrationType = (registrationNumber) => {
  if (!registrationNumber) return 'DOT/MC';
  const numStr = String(registrationNumber).toUpperCase();
  if (numStr.includes('MC-') || numStr.startsWith('MC')) {
    return 'MC';
  }
  return 'DOT';
};

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1.5),
    backgroundColor: '#fdfdfd',
    border: '1px solid #e0e0e0',
    marginBottom: theme.spacing(2),
}));

const CompactTextField = styled(TextField)(() => ({
    '& .MuiInputBase-root': { height: 36, fontSize: '0.85rem' },
    '& .MuiInputLabel-root': { fontSize: '0.7rem' },  
}));

const CompactFormControl = styled(FormControl)(() => ({
    minHeight: 36,
    '& .MuiInputLabel-root': { fontSize: '0.75rem' },
}));

const CompactSelect = styled(Select)(() => ({
    fontSize: '0.75rem',
    '& .MuiSelect-select': {
        padding: '6px 8px',
        display: 'flex',
        alignItems: 'center',
    },
    height: 36,
}));

const PostButton = styled(Button)(({ theme }) => ({
    borderRadius: 6,
    padding: '6px 16px',
    fontSize: '0.8rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    '&:disabled': {
        background: '#b0b0b0',
        color: '#fff',
    },
    '&:enabled': {
       
        backgroundColor: theme.palette.primary.main,
        color: 'white',
    },
    '&:hover:enabled': {
        backgroundColor: theme.palette.secondary.main, 
    },
}));

const ClearButton = styled(Button)(({ theme }) => ({
    fontSize: '0.8rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 6,
    color: theme.palette.primary.main,
    backgroundColor: 'transparent',
    '&:hover': { backgroundColor: 'rgba(17, 34, 77, 0.05)' },
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.primary.main,
    '&:hover': { backgroundColor: 'rgba(17, 34, 77, 0.08)' },
}));

const StyledTableRow = styled(TableRow)(({ index }) => ({
    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f7f9fc',
}));

const DetailSection = ({ title, children }) => (
    <Box sx={{ pr: 2, mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: "bold",
          fontSize: "0.9rem",
          color: "#11224d",
          mb: 0.5,
          borderBottom: '1px solid #ddd',
          pb: 0.5,
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
);

const RepostButton = styled(Button)(({ theme, disabled }) => ({
    minWidth: 0, 
    padding: '4px 5px',
    fontSize: '0.6rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: disabled ? theme.palette.grey[400] : theme.palette.success.main,
    color: 'white',
    '&:hover': {
        backgroundColor: disabled ? theme.palette.grey[400] : theme.palette.success.dark,
    },
}));


const StatusButton = styled(Button)(({ theme, active }) => ({
  minWidth: 0,
  padding: '4px 5px',
  fontSize: '0.6rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  borderRadius: theme.shape.borderRadius,
  marginLeft: '4px',

  backgroundColor: active ? theme.palette.grey[500] : theme.palette.info.main,
  color: 'white',
  
  '&:hover': {
 
    backgroundColor: active ? theme.palette.grey[700] : theme.palette.success.dark,
  },
}));

const TableProgressBar = styled('div')(({ theme }) => ({
  width: '100%',
  height: '4px',
  backgroundColor: '#ffb300',
  overflow: 'hidden',
  position: 'relative',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-30%',
    width: '30%',
    height: '100%',
    background: `
      linear-gradient(
        90deg,
        transparent,
        rgba(255,255,255,0.6),
        rgba(255,255,255,0.9),
        rgba(255,255,255,0.6),
        transparent
      )
    `,
    animation: 'windowsShimmer 3s linear infinite',
  },

  '@keyframes windowsShimmer': {
    '0%': { left: '-30%' },
    '100%': { left: '100%' },
  },
}));

const LoadRow = ({ load, idx, handleEdit, handleDelete, handleRepost, handleToggleStatus, repostCooldowns, currentUser, repostingIds }) => {
    const [open, setOpen] = useState(false);

    return (
        <>
            <StyledTableRow index={idx} hover onClick={() => setOpen(!open)} sx={{ cursor: 'pointer' }}>
                <TableCell padding="checkbox" sx={{ borderBottom: open ? 'none' : null }}>
                    <IconButton size="small" sx={{ p: 0, color: 'primary.main' }}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary' , textAlign: 'center'}}>
                    {formatRelativeTime(load.timePosted)}
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
                   {load.pickupDate ? dayjs(load.pickupDate).format('MM/DD/YYYY') : 'N/A'}
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem', textAlign: 'center' }}>{load.origin}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', textAlign: 'center' }}>{load.destination}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem' , textAlign: 'center'}}>{load.equipment}</TableCell> 
               <TableCell sx={{ fontSize: '0.75rem', fontWeight: '700', textAlign: 'center' }}>
  {load.offerRate
    ? (load.rateType === 'Flat'
        ? `$${Number(load.offerRate).toFixed(0)}`
        : `$${Number(load.offerRate).toFixed(0)}/mi`)
    : '–'}
</TableCell>

               
    <TableCell sx={{ textAlign: 'center' }}>
        <span style={{ 
            fontWeight: 600, 
            color: load.isActive ? '#00c853' : '#ff9800', 
            fontSize: '0.67rem',
            padding: '2px 5px',
            borderRadius: '4px',
            backgroundColor: load.isActive ? '#e8f5e9' : '#fff3e0',
            whiteSpace: 'nowrap'
        }}>
            {load.isActive ? 'ACTIVE' : 'DEACTIVE'}
        </span>
    </TableCell>
                <TableCell 
                align="center" 
                sx={{ borderBottom: open ? 'none' : null, py: 0.5, px: 0.5}} 
                onClick={(e) => e.stopPropagation()}>
                     <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}></Box>
                    <StyledIconButton onClick={() => handleEdit(load.id)} size="small"><EditIcon fontSize="inherit" /></StyledIconButton>
                    <StyledIconButton onClick={() => handleDelete(load.id)} size="small"><DeleteIcon fontSize="inherit" /></StyledIconButton>
                    <RepostButton
                        
                            onClick={() => handleRepost(load.id)} 
                            size="small"
                            disabled={repostingIds.has(load.id)}
                        >
                            {repostingIds.has(load.id) ? <CircularProgress size={16} color="inherit" /> : 'REPOST'}
                        </RepostButton>
                 
                    <StatusButton
              active={load.isActive}
              onClick={() => handleToggleStatus(load.id, load.isActive)}
              size="small"
          >
              {load.isActive ? 'DEACTIVATE' : 'ACTIVATE'} 
          </StatusButton>

                </TableCell>
            </StyledTableRow>

              <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                <Collapse in={open} timeout="auto" unmountOnExit>
            <Card
                sx={{
                    border: "1px solid #f0f0f0", 
                    borderRadius: 1, 
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    backgroundColor: "#ffffff",
                }}
            >
                <CardContent sx={{ p: 1.5 }}> 
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: "bold",
                            mb: 1,
                            fontSize: "1rem", 
                            color: "#11224d", 
                        }}
                    >
                        {load.origin} → {load.destination} ({load.tripMiles ? Math.round(Number(load.tripMiles)) : "N/A"} mi)
                    </Typography>
                    <Divider sx={{ my: 1 }} /> 
                    <Box
                        sx={{
                            display: "flex",
                            width: "100%",
                            mt: 1,
                        }}
                    >
                       
                        <Box sx={{ flex: '3 1 0%', pr: 3 }}>
                            <DetailSection title="Trip">
                                
                                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            mr: 1.5,
                                            mt: 0.5,
                                        }}
                                    >
                                      
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                border: '2px solid #1976d2', 
                                                backgroundColor: 'white',
                                            }}
                                        />
                                        
                                        <Box
                                            sx={{
                                                width: 0,
                                                height: 46, 
                                                borderLeft: '1px dashed #bdbdbd',
                                                position: 'relative',
                                                '&::after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    bottom: -5, 
                                                    left: -2.5,
                                                    width: 0,
                                                    height: 0,
                                                    borderLeft: '4px solid transparent',
                                                    borderRight: '4px solid transparent',
                                                    borderTop: '4px solid #bdbdbd',
                                                }
                                            }}
                                        />
                                      
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                backgroundColor: '#1976d2', 
                                            }}
                                        />
                                    </Box>

                                    <Box>
                                        <Typography variant="caption" sx={{ display: "block" }}>
                                            <span style={{ fontWeight: "bold" }}>{load.origin}</span> 
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                                           {load.pickupDate ? dayjs(load.pickupDate).format('MM/DD/YYYY') : 'N/A'} - (Pickup Time: {load.pickupTime ? dayjs(load.pickupTime, 'HH:mm').format('hh:mm A') : "N/A"})
 
                                        </Typography>
                                        <Typography  variant="caption" sx={{ display: "block", mt: 2  }}>
                                           <span style={{ fontWeight: "bold" }}>{load.destination}</span> - (Dropoff Time: {load.dockTime ? dayjs(load.dockTime, 'HH:mm').format('hh:mm A') : "N/A"})
                                        </Typography>
                                    </Box>
                                </Box>
                              
                                <Box sx={{ mt: 1 }}> 
                                    <Typography variant="caption" sx={{ display: "block" }}>
                                        <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>Equipment:</span>{" "}
                                        {load.equipment}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: "block" }}>
                                        <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>Load:</span>{" "}
                                        {load.fullOrPartial}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: "block" }}>
                                        <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>Feet:</span>{" "}
                                        {load.length ? `${load.length} ft` : "N/A"}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: "block" }}>
                                        <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>Weight:</span>{" "}
                                        {load.weight ? `${load.weight} lbs` : "N/A"}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: "block" }}>
                                        <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>Commodity:</span>{" "}
                                        {load.commodity || "N/A"}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: "block" }}>
                                        <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>Reference ID:</span>{" "}
                                        {load.referenceId || "N/A"}
                                    </Typography>
                                </Box>
                            </DetailSection>
                        </Box>

                       
                        <Box sx={{ flex: '5 1 0%' }}>
                            
                         
<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>

    <Box sx={{ flex: '1 1 50%', pr: 2 }}>
        <DetailSection title="Rate & Payment">

            <Typography variant="caption" sx={{ display: "block" }}>
                <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>
                    Offer Rate ({load.rateType === "Flat" ? "Flat" : "Per Mile"}):
                </span>{" "}
                {load.offerRate
                    ? `$${Number(load.offerRate).toFixed(0)}`
                    : "-"}
            </Typography>

            <Typography variant="caption" sx={{ display: "block" }}>
                <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>
                    Rate / Mile:
                </span>{" "}
                {load.tripMiles && load.offerRate ? (
                    load.rateType === "Flat"
                        ? `$${(Number(load.offerRate) / Number(load.tripMiles)).toFixed(0)}`
                        : `$${Number(load.offerRate).toFixed(0)}`
                ) : (
                    "-"
                )}
            </Typography>

            <Typography variant="caption" sx={{ display: "block" }}>
                <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>Trip Miles:</span>{" "}
                {load.tripMiles ? `${Math.round(Number(load.tripMiles))} mi` : "N/A"}
            </Typography>

            <Typography variant="caption" sx={{ display: "block" }}>
                <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>Total Rate:</span>{" "}
                {load.offerRate && load.tripMiles ? (
                    load.rateType === "Flat"
                        ? `$${Number(load.offerRate).toFixed(0)}`
                        : `$${(Number(load.offerRate) * Number(load.tripMiles)).toFixed(0)}`
                ) : (
                    "-"
                )}
            </Typography>

                                        {currentUser?.role !== 'broker' && (
                                            <>
                                                <Box sx={{ mt: 1 }} />
                                                <Typography variant="caption" sx={{ display: "block" }}>
                                                    <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>Payment:</span>{" "}
                                                    {load.paymentMethod}
                                                </Typography>
                                                {load.paymentMethod === "Custom Terms" && (
                                                    <Typography variant="caption" sx={{ display: "block" }}>
                                                        <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>Terms:</span>{" "}
                                                        {load.customTermsDetails}
                                                    </Typography>
                                                )}
                                            </>
                                        )}
                                    </DetailSection>
                                </Box>

                                <Box sx={{ flex: '1 1 50%' }}>
                                    <DetailSection title="Company">
                                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                            <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                                                {currentUser?.company_name || 'Your Company'} 
                                            </Typography>
                                        </Box>
                                       {load.dot_number && (
  <Typography variant="caption" sx={{ display: "block" }}>
    <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>
      DOT Number:
    </span>{" "}
    {load.dot_number}
  </Typography>
)}

{load.mc_number && load.mc_number !== load.dot_number && (
  <Typography variant="caption" sx={{ display: "block" }}>
    <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>
      MC Number:
    </span>{" "}
    {load.mc_number.toString().replace(/^MC-?/gi, '').trim()}
  </Typography>
)}
{/*                                       
                                       {currentUser?.role === 'broker' && (
  <Typography variant="caption" sx={{ display: "block" }}>
    <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>
      {getRegistrationType(load.registration_number || currentUser.mc_number || currentUser.dot_number)} :
    </span>{" "}
    {load.registration_number || currentUser.mc_number || currentUser.dot_number || 'N/A'}
  </Typography>
)} */}

                                
                                        <Typography variant="caption" sx={{ display: "block" }}>
                                            <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>Contact Name:</span>{" "}
                                            {currentUser?.company_name || currentUser?.dba_name || 'Dispatch'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: "block" }}>
                                            <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>
                                                {load.contactMethod?.includes("@") ? "Email" : "Phone"}:
                                            </span>{" "}
                                           {load.contactMethod?.includes("@") 
        ? load.contactMethod 
        : formatPhoneNumber(load.contactMethod)
    }
                                        </Typography>
                                    </DetailSection>
                                </Box>
                            </Box>
                            
                            
                            <DetailSection title="Notes">
                                <Typography variant="caption">
                                    {load.comment || "No additional comments provided."}
                                </Typography>
                            </DetailSection>

                        </Box>
                    </Box>

                </CardContent>
            </Card>
        </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) {
    return '';
  }
  let cleaned = ('' + phoneNumber).replace(/\D/g, '');

  if (cleaned.length === 11 && cleaned.startsWith('0')) {
    cleaned = cleaned.substring(1); 
  }

  if (cleaned.length === 10) {
    const areaCode = cleaned.substring(0, 3);
    const middle = cleaned.substring(3, 6);
    const last = cleaned.substring(6, 10);

    return `(${areaCode}) ${middle}-${last}`;
  }

  return phoneNumber; 
};

// const formatPhoneNumber = (phoneNumber) => {
//   if (!phoneNumber) return '';
//   const cleaned = ('' + phoneNumber).replace(/\D/g, '');
//   if (cleaned.length === 10) {
//     return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6, 10)}`;
//   }
//   return phoneNumber;
// };

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
const geocodingClient = MapboxClient({ accessToken: MAPBOX_TOKEN });

const suggestionsCache = new Map();
let debounceTimer;

const PostLoads = () => {

    const [validatingOrigin, setValidatingOrigin] = useState(false);
    const [formOpen, setFormOpen] = useState(true);
    const [validatingDestination, setValidatingDestination] = useState(false);
    const originInputRef = useRef();
    const destinationInputRef = useRef();

const validateAndFormatLocation = async (input, setField, setErrorField, fieldName, setInputValue, coordField, currentData) => {
  const trimmed = input?.trim();
  if (!trimmed) return false;

  if (currentData && currentData[fieldName] === trimmed && currentData[coordField]) return true;

  try {
    const res = await geocodingClient
      .forwardGeocode({ query: trimmed, limit: 1, countries: ['US'] })
      .send();

    const feature = res.body.features[0];
    if (!feature) {
      setErrorField(prev => ({
        ...prev,
        [fieldName]: 'Enter a valid city, state or US zip code',
      }));
      setField(prev => ({ ...prev, [fieldName]: '' }));
      if (setInputValue) setInputValue('');
      return false;
    }

    let city = '';
    let stateShort = '';
    if (feature.context) {
      const cityContext = feature.context.find(c => c.id.startsWith('place'));
      if (cityContext?.text) city = cityContext.text;

      const state = feature.context.find(c => c.id.startsWith('region'));
      stateShort =
        state?.short_code && state.short_code.split('-').length > 1
          ? state.short_code.split('-')[1].toUpperCase()
          : '';
    }

    if (!city && feature.text) city = feature.text;

    if (!(city && stateShort)) {
      setErrorField(prev => ({
        ...prev,
        [fieldName]: 'Enter a valid city, state or US zip code',
      }));
      setField(prev => ({ ...prev, [fieldName]: '' }));
      if (setInputValue) setInputValue('');
      return false;
    }

    const formatted = `${city}, ${stateShort}`;

    setField(prev => ({ 
        ...prev, 
        [fieldName]: formatted,
        [coordField]: feature.center 
    }));
    if (setInputValue) setInputValue(formatted);
    setErrorField(prev => ({ ...prev, [fieldName]: '' }));
    return true;
  } catch (err) {
    setErrorField(prev => ({
      ...prev,
      [fieldName]: 'Location validation failed',
    }));
    setField(prev => ({ ...prev, [fieldName]: '' }));
    if (setInputValue) setInputValue('');
    return false;
  }
};


    const [formData, setFormData] = useState({
        pickupDate: dayjs(),
        pickupTime: '',
        origin: '',
        destination: '',
        dockTime: '',
        fullOrPartial: 'Full',
        length: '',
        weight: '',
        offerRate: '',
        equipment: '', 
        commodity: '',
        tripMiles: '',
        customTermsDetails: '',
        rateType: 'Flat',
        contactMethod: '', 
        referenceId: '',
        originCoords: null,
        destinationCoords: null,
        paymentMethod: '50% at Pickup / 50% at Delivery (Preferred)',
        comment: '',
    });
    const [errors, setErrors] = useState({});
    const [postedLoads, setPostedLoads] = useState([]);
    const [submissionStatus, setSubmissionStatus] = useState({ open: false, message: '', severity: 'success' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [repostingIds, setRepostingIds] = useState(new Set());
    const [isDataOutdated, setIsDataOutdated] = useState(false);

    const [paymentMethods, setPaymentMethods] = useState([]);
    const [equipmentTypes, setEquipmentTypes] = useState([]);
    const REPOST_COOLDOWNS_LOADS_KEY = 'repostCooldowns_loads';

    const [repostCooldowns, setRepostCooldowns] = useState(() => {
        try {
            const stored = localStorage.getItem(REPOST_COOLDOWNS_LOADS_KEY);
            if (!stored) return {};

            const parsed = JSON.parse(stored);
            const now = dayjs();
            const cooldownMinutes = 15;
            const cleaned = {};

            for (const id in parsed) {
                if (now.diff(dayjs(parsed[id])) < cooldownMinutes * 60 * 1000) {
                    cleaned[id] = parsed[id];
                }
            }
            return cleaned;
        } catch (e) {
            console.error("Failed to read or parse repost cooldowns from localStorage", e);
            return {};
        }
    });

    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: () => {},
    }); 

     const [currentUser, setCurrentUser] = useState(null);
     const [isBroker, setIsBroker] = useState(false);
     useEffect(() => {
        const storedUser = localStorage.getItem('user');
        const storedRole = localStorage.getItem('role');
        if (storedUser) {
            const parsedUser = JSON.parse(storedUser);
            setCurrentUser(parsedUser);
            setIsBroker(storedRole === 'broker');
            
            setFormData(prev => ({
                ...prev,
                contactMethod: parsedUser.phone_number || parsedUser.email || ''
            }));
        }
    }, []); 

    useEffect(() => {
        try {
            localStorage.setItem(REPOST_COOLDOWNS_LOADS_KEY, JSON.stringify(repostCooldowns));
        } catch (e) {
            console.error("Failed to save repost cooldowns to localStorage", e);
        }
    }, [repostCooldowns]);

    useEffect(() => {
        const getPaymentMethods = async () => {
            try {
                const token = localStorage.getItem('jwtToken');
                const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
                const { data } = await axios.get(`${API_BASE}/api/public/payment-methods`, { headers: { Authorization: `Bearer ${token}` } });
               
                setPaymentMethods(data);
            } catch (error) {
                console.error("Could not load payment methods for dropdown.", error);
            }
        };
        getPaymentMethods();
    }, []);

    useEffect(() => {
        const getEquipment = async () => {
            try {
                const token = localStorage.getItem('jwtToken');
                const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
                const { data } = await axios.get(`${API_BASE}/api/public/equipment`, { 
                    headers: { Authorization: `Bearer ${token}` } 
                });
                setEquipmentTypes(data);
            } catch (error) {
                console.error("Could not load equipment types for dropdown.", error);
            }
        };
        getEquipment();
    }, []);

    const [originSuggestions, setOriginSuggestions] = useState([]);
const [destinationSuggestions, setDestinationSuggestions] = useState([]);

    const [tick, setTick] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setTick(t => t + 1);
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        fetchPostedLoads();
    }, []); 

const getSuggestions = async (query) => {
  if (!query || query.trim().length < 3) return [];
  const cacheKey = query.trim().toLowerCase();
  if (suggestionsCache.has(cacheKey)) return suggestionsCache.get(cacheKey);

  return new Promise((resolve) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(async () => {
  try {
    const res = await geocodingClient.forwardGeocode({
      query,
      autocomplete: true,
      limit: 5,
      countries: ["US"],
      types: ["place"], // This prevents full states from being suggested
    }).send();
    const results = res.body.features.map(feature => {
      const city = feature.text;
      const stateObj = feature.context?.find(c => c.id.startsWith("region"));
      
      // Get the short code (TX) instead of the full name (Texas)
      const stateShort = stateObj?.short_code 
        ? stateObj.short_code.split("-")[1].toUpperCase() 
        : "";

      return {
        ...feature,
        // Formats the dropdown display as "City, ST"
        place_name: city && stateShort ? `${city}, ${stateShort}` : city,
      };
    });
    suggestionsCache.set(cacheKey, results);
    resolve(results);
  } catch (err) {
    console.error("Mapbox Autocomplete error:", err);
    resolve([]);
  }
    }, 300);
  });
};

const calculateTripMiles = async (origin, destination, existingOriginCoords, existingDestCoords) => {
    try {
        let originCoord = existingOriginCoords;
        let destCoord = existingDestCoords;

        // Only geocode if we don't have the coordinates from validation yet
        if (!originCoord) {
            const res = await geocodingClient.forwardGeocode({ query: origin, limit: 1 }).send();
            originCoord = res.body.features[0]?.geometry?.coordinates;
        }
        if (!destCoord) {
            const res = await geocodingClient.forwardGeocode({ query: destination, limit: 1 }).send();
            destCoord = res.body.features[0]?.geometry?.coordinates;
        }

        if (!originCoord || !destCoord) return "";

        const directionsRes = await fetch(
            `https://api.mapbox.com/directions/v5/mapbox/driving/${originCoord[0]},${originCoord[1]};${destCoord[0]},${destCoord[1]}?access_token=${MAPBOX_TOKEN}`
        );
        const data = await directionsRes.json();

        if (data.routes && data.routes[0]) {
            const miles = (data.routes[0].distance / 1609.34).toFixed(0);
            return miles;
        }
        return "";
    } catch (err) {
        console.error("Mapbox Directions error:", err);
        return "";
    }
};
    useEffect(() => {
    const fetchTripMiles = async () => {
        if (formData.origin && formData.destination) {
            const miles = await calculateTripMiles(
                formData.origin, 
                formData.destination, 
                formData.originCoords, 
                formData.destinationCoords
            );
            setFormData(prev => ({ ...prev, tripMiles: miles }));
        }
    };
    fetchTripMiles();
}, [formData.origin, formData.destination]);

const fetchPostedLoads = async () => {
  try {
    const token = localStorage.getItem('jwtToken');

    const API_BASE = process.env.REACT_APP_API_BASE;

    const res = await fetch(`${API_BASE}/api/posts?limit=100`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    });

    if (!res.ok) {
    
      const text = await res.text();
      console.error('Raw server response:', text.slice(0, 200));
      throw new Error(`Failed to fetch posts, status: ${res.status}`);
    }

    const data = await res.json();
    setPostedLoads(data.results || []);
  } catch (err) {
    console.error('Fetch posts error:', err);
    setSubmissionStatus({
      open: true,
      message: 'Could not load existing posts.',
      severity: 'error'
    });
  }
};

// Cross-tab data synchronization
useEffect(() => {
    const channel = new BroadcastChannel('app_data_sync');
    
    channel.onmessage = (event) => {
        if (event.data === 'DATA_CHANGED') {
            setIsDataOutdated(true);
        }
    };

    return () => {
        channel.close();
    };
}, []);

useEffect(() => {
    const checkDataUpdate = () => {
        if (document.visibilityState === 'visible' && isDataOutdated) {
            fetchPostedLoads();
            setIsDataOutdated(false);
        }
    };

    window.addEventListener('focus', checkDataUpdate);
    document.addEventListener('visibilitychange', checkDataUpdate);

    return () => {
        window.removeEventListener('focus', checkDataUpdate);
        document.removeEventListener('visibilitychange', checkDataUpdate);
    };
}, [isDataOutdated]);

    const validateForm = async () => {
  let newErrors = {};

  const originVal = formData.origin.trim();
  const destinationVal = formData.destination.trim();
  const lengthVal = Number(formData.length);
  const weightVal = Number(formData.weight);
  const offerRateVal = Number(formData.offerRate);
  const refIdVal = formData.referenceId;
  const tripMilesVal = Number(formData.tripMiles);

  if (!formData.pickupDate) newErrors.pickupDate = "Pickup date is required";
  if (!originVal) newErrors.origin = "Origin is required";
  if (!destinationVal) newErrors.destination = "Destination is required";

  if (originVal) {
    setValidatingOrigin(true);
    const valid = await validateAndFormatLocation(
      originVal,
      setFormData,
      setErrors,
      'origin',
     (val) => setFormData(prev => ({ ...prev, origin: val })),
      'originCoords'
    );
    setValidatingOrigin(false);
    if (!valid) newErrors.origin = 'Enter a valid city, state or US zip code';
  }

  if (destinationVal) {
    setValidatingDestination(true);
    const valid = await validateAndFormatLocation(
      destinationVal,
      setFormData,
      setErrors,
      'destination',
      (val) => setFormData(prev => ({ ...prev, destination: val })),
      'destinationCoords'
    );
    setValidatingDestination(false);
    if (!valid) newErrors.destination = 'Enter a valid city, state or US zip code';
  }

  if (!formData.equipment) newErrors.equipment = "Equipment is required";
  if (!formData.fullOrPartial) newErrors.fullOrPartial = "Load Type is required";
  if (!formData.rateType) newErrors.rateType = "Rate Type is required";
  if (!formData.length) newErrors.length = "Feet is required";
  if (!formData.weight) newErrors.weight = "Weight is required";

  if (formData.paymentMethod === 'Custom Terms' && !formData.customTermsDetails) {
    newErrors.customTermsDetails = "Custom Terms details are required";
  }

  if (formData.offerRate !== '' && (offerRateVal < 0 || offerRateVal >= 1000000)) {
    newErrors.offerRate = "Rate must be a positive number or greater than 0";
  }

  if (formData.length !== '' && (lengthVal < 0 || lengthVal > 999)) { 
    newErrors.length = "Feet must be between 0–999 ft"; 
  }

  if (formData.weight !== '' && (weightVal < 0 || weightVal > 99999999)) {
    newErrors.weight = "Weight must be between 0–99,999,999 lbs";
  }

  if (formData.tripMiles !== '' && (tripMilesVal < 0)) {
    newErrors.tripMiles = "Trip miles must be a positive number";
  }

  if (refIdVal && refIdVal.length > 20) {
    newErrors.referenceId = "Max 20 characters allowed";
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};


    const handleChange = (e) => {
    const { name, value } = e.target;
    const numValue = parseFloat(value);

    if (name === 'length') {
        if (value === '' || (numValue >= 0 && numValue <= 999)) {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        return; 
    }

    if (name === 'weight') {
      
        if (value === '' || (numValue >= 0 && numValue <= 99999999)) {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        return; 
    }

    setFormData(prev => ({ ...prev, [name]: value }));
};

    const handleDateChange = (date) => setFormData(prev => ({ ...prev, pickupDate: date }));
    
    const handleClear = () => {
        setFormData({
            pickupDate: dayjs(), pickupTime: '', origin: '', destination: '', dockTime: '',
            fullOrPartial: 'Full', length: '', weight: '', offerRate: '', equipment: '', 
            commodity: '', tripMiles: '', paymentMethod: '50% at Pickup / 50% at Delivery (Preferred)',
            customTermsDetails: '', rateType: 'Flat',contactMethod: currentUser?.phone_number || currentUser?.email || '',
            referenceId: '', comment: ''
        });
        setErrors({});
    };

    const handleCloseSnackbar = () => setSubmissionStatus({ ...submissionStatus, open: false });

        const handleSubmit = async () => {
            if (isSubmitting) return;
            setIsSubmitting(true);

            const valid = await validateForm();
            if (!valid) {
                setSubmissionStatus({ open: true, message: "Please fix the errors before submitting.", severity: 'error' });
                setIsSubmitting(false);
                return;
            }
    const posterDetails = {
      shipper_company: currentUser?.company_name || currentUser?.dba_name || null,
      shipper_address: currentUser?.street ? `${currentUser.street}${currentUser?.city_st_zip ? ', ' + currentUser.city_st_zip : ''}` : null,
      shipper_phone: currentUser?.phone_number || null,
      shipper_email: currentUser?.email || null,
    };

    const payload = {
      ...formData,
      pickupDate: formData.pickupDate ? formData.pickupDate.format('YYYY-MM-DD') : null,
      details: { ...(formData.details || {}), ...posterDetails }
    };

    try {
        const token = localStorage.getItem('jwtToken'); 
        const res = await fetch(`${process.env.REACT_APP_API_BASE || ''}/api/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' ,'Authorization': token ? `Bearer ${token}` : '',},
        body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error('Failed to post');
        const created = await res.json();

        const now = dayjs().toISOString();
        setRepostCooldowns(prev => ({ ...prev, [created.id]: now }));
        setPostedLoads(prev => [created, ...prev]);
    setSubmissionStatus({ open: true, message: "Load posted successfully! ✅", severity: 'success' });
    const channel = new BroadcastChannel('app_data_sync');
    channel.postMessage('DATA_CHANGED');
    channel.close();
    await fetchPostedLoads(); 
    handleClear();
    } catch (err) {
        console.error('Post load error', err);
        setSubmissionStatus({ open: true, message: "Failed to post load", severity: 'error' });
    } finally {
        setIsSubmitting(false);
    }
};


 const handleEdit = async (id) => {
  try {
    const token = localStorage.getItem('jwtToken');     
    
    const res = await fetch(`${process.env.REACT_APP_API_BASE || ''}/api/posts/${id}`, { headers: { 'Authorization': token ? `Bearer ${token}` : '' }});
    if (!res.ok) throw new Error('Fetch failed');
    const load = await res.json();
    
    setFormData({
      ...load,
     
      pickupDate: load.pickupDate 
                          ? dayjs(load.pickupDate).startOf('day') 
                          : dayjs().startOf('day')
    });
    
    const del = await fetch(`${process.env.REACT_APP_API_BASE || ''}/api/posts/${id}`, { 
        method: 'DELETE',
        headers: {
            
            'Authorization': token ? `Bearer ${token}` : '', 
        },
         });
    if (del.ok) {
      const channel = new BroadcastChannel('app_data_sync');
      channel.postMessage('DATA_CHANGED');
      channel.close();
      setPostedLoads(prev => prev.filter(l => l.id !== id));
    }
    setFormOpen(true);
    setSubmissionStatus({ open: true, message: "Load moved back to form for editing.", severity: 'info' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } catch (err) {
    console.error('edit error', err);
    setSubmissionStatus({ open: true, message: "Failed to load for edit", severity: 'error' });
    await fetchPostedLoads();
  }
};

    
    const handleDelete = async (id) => {
  try {
    const token = localStorage.getItem('jwtToken'); 
    const res = await fetch(`${process.env.REACT_APP_API_BASE || ''}/api/posts/${id}`, { 
        method: 'DELETE',
        headers: {
         
            'Authorization': token ? `Bearer ${token}` : '', 
        },
    });
    if (!res.ok) throw new Error('Delete failed');
    setPostedLoads(prev => prev.filter(l => l.id !== id));
    const channel = new BroadcastChannel('app_data_sync');
    channel.postMessage('DATA_CHANGED');
    channel.close();
   
setSubmissionStatus({ open: true, message: "Load deleted successfully!", severity: 'warning' });
await fetchPostedLoads();
  } catch (err) {
    console.error('delete error', err);
    setSubmissionStatus({ open: true, message: "Failed to delete load", severity: 'error' });
  }
};

    
  const handleRepost = async (id) => {
   
    const now = dayjs();
    const lastRepost = repostCooldowns[id] ? dayjs(repostCooldowns[id]) : null;
    const cooldownMinutes = 15; 
    const cooldownDuration = cooldownMinutes * 60 * 1000;

    if (lastRepost && now.diff(lastRepost) < cooldownDuration) {
        const timeLeft = dayjs.duration(cooldownDuration - now.diff(lastRepost));
        setSubmissionStatus({ 
            open: true, 
            message: `Repost will be allowed after ${timeLeft.minutes()}m ${timeLeft.seconds()}s. ⏳`, 
            severity: 'warning' 
        });
        return;
    }

    setRepostingIds(prev => new Set(prev).add(id));

    try {
        const token = localStorage.getItem('jwtToken'); 
        const load = postedLoads.find(l => l.id === id);
        if (!load) return;
        
     
        const copyPayload = { ...load };
     
        delete copyPayload.id;
        delete copyPayload.timePosted;
        delete copyPayload.postedTime;
        delete copyPayload.rate;
        delete copyPayload.totalRate;
        delete copyPayload.deadhead_origin_miles;
        delete copyPayload.deadhead_destination_miles;
        delete copyPayload.isActive; 
        
        if (copyPayload.pickupDate) {
    copyPayload.pickupDate = dayjs(copyPayload.pickupDate).format('YYYY-MM-DD');
}
        
        const res = await fetch(`${process.env.REACT_APP_API_BASE || ''}/api/posts`, {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                'Authorization': token ? `Bearer ${token}` : '',
            },
            body: JSON.stringify(copyPayload),
        });

      
        if (!res.ok) {
            
            let errorDetails = 'Unknown server error.';
            try {
                const errorJson = await res.json();
                errorDetails = errorJson.message || JSON.stringify(errorJson);
            } catch (e) {
                
                errorDetails = await res.text();
            }
            console.error('Repost failed with status:', res.status, 'Error Body:', errorDetails);
            throw new Error('Repost failed: ' + errorDetails);
        }

        const created = await res.json();
        
        setPostedLoads(prev => [created, ...prev]);
        setRepostCooldowns(prev => ({ 
            ...prev, 
            [id]: now.toISOString(), 
            [created.id]: now.toISOString(), 
        })); 
        
        setSubmissionStatus({ open: true, message: "Load reposted successfully! ✅", severity: 'success' });
        const channel = new BroadcastChannel('app_data_sync');
        channel.postMessage('DATA_CHANGED');
        channel.close();
        await fetchPostedLoads();
    } catch (err) {
    
        console.error('repost error', err);
        setSubmissionStatus({ open: true, message: "Failed to repost load", severity: 'error' });
    } finally {
        setRepostingIds(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    }
};

  const handleToggleStatus = async (loadId) => {
    try {
        const token = localStorage.getItem('jwtToken');
        const API_BASE = process.env.REACT_APP_API_BASE;

        const res = await fetch(`${API_BASE}/api/posts/${loadId}/status`, {
            method: 'PATCH',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        });

        if (!res.ok) {
            const errorData = await res.json();
            throw new Error(errorData.message || 'Failed to update load status');
        }

        const updatedLoad = await res.json();

        setPostedLoads(prevLoads =>
            prevLoads.map(load =>
                load.id === loadId ? updatedLoad : load
            )
        );

        setSubmissionStatus({
            open: true,
            message: `Load is now ${updatedLoad.isActive ? 'Active' : 'Deactivated'}.`,
            severity: 'info'
        });

        const channel = new BroadcastChannel('app_data_sync');
        channel.postMessage('DATA_CHANGED');
        channel.close();
    } catch (err) {
        console.error('Toggle status error:', err);
        setSubmissionStatus({
            open: true,
            message: 'Could not update load status.',
            severity: 'error'
        });
    }
  };

    const handleEditWithConfirm = (id) => {
        setConfirmDialog({
            open: true,
            title: 'Confirm Edit',
            message: 'Do you want to move this load to the "Post a load" form for editing? It will be removed from the list temporarily.',
            onConfirm: () => {
                handleEdit(id);
                setConfirmDialog({ ...confirmDialog, open: false });
            }
        });
    };

    const handleDeleteWithConfirm = (id) => {
        setConfirmDialog({
            open: true,
            title: 'Confirm Deletion',
            message: 'Are you sure you want to delete this load post? This action cannot be undone.',
            onConfirm: () => {
                handleDelete(id);
                setConfirmDialog({ ...confirmDialog, open: false });
            }
        });
    };

  const handleRepostWithConfirm = (id) => {
    handleRepost(id);
};


    const handleToggleStatusWithConfirm = (loadId, isActive) => {
        const action = isActive ? 'DEACTIVATE' : 'ACTIVATE';
        setConfirmDialog({
            open: true,
            title: `Confirm ${action}`,
            message: `Are you sure you want to ${action.toLowerCase()} this load post?`,
            onConfirm: () => {
                handleToggleStatus(loadId);
                setConfirmDialog({ ...confirmDialog, open: false });
            }
        });
    };


    return (
        <ThemeProvider theme={theme}>
            <Dialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
                aria-labelledby="confirm-dialog-title"
                aria-describedby="confirm-dialog-description"
                maxWidth="xs" 
                fullWidth 
            >
                <DialogTitle 
                    id="confirm-dialog-title"
                    sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#11224d' }}
                >
                    {confirmDialog.title}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText 
                        id="confirm-dialog-description"
                        sx={{ fontSize: '0.9rem' }}
                    >
                        {confirmDialog.message}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ padding: '8px 16px' }}>
                    <Button 
                        onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
                        size="small"
                        sx={{
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            borderRadius: 2,
                            padding: '6px 16px',
                            border: `1px solid ${theme.palette.primary.main}`,
                            color: theme.palette.primary.main,
                            backgroundColor: 'transparent',
                            '&:hover': { backgroundColor: 'rgba(17, 34, 77, 0.05)' },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={confirmDialog.onConfirm} 
                        autoFocus 
                        size="small"
                        sx={{
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            borderRadius: 2,
                            padding: '6px 16px',
                            background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            color: 'white',
                            '&:hover': {
                                background: `linear-gradient(to right, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                            },
                        }}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
            <Container maxWidth="xl" sx={{ mt: 0.5, mb: 2 }}>
                
                <StyledPaper>
                    {!formOpen && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                cursor: 'pointer',
                            }}
                            onClick={() => setFormOpen(true)}
                        >
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#11224d' }}>
                                Post a Load
                            </Typography>
                            <Tooltip title="Expand Form">
                                <IconButton size="small">
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <KeyboardArrowDownIcon sx={{ fontSize: '1.2rem', color: 'grey.500' }} />
                                        <KeyboardArrowDownIcon sx={{ fontSize: '1.2rem', mt: -1.5, color: 'grey.500' }} />
                                        <KeyboardArrowDownIcon sx={{ fontSize: '1.2rem' , mt: -1.5, }} />
                                    </Box>
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                    <Collapse in={formOpen}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(160px, 1fr))' },
                            gap: 1.5,
                            alignItems: 'start',
                            mt: 1
                        }}
                    >
                       
                        <Box>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Pickup Date "
                                     size="small"
                                    value={formData.pickupDate}
                                    onChange={handleDateChange}
                                    minDate={dayjs()}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true, size: 'small', required: true, fontSize: '0.5rem',
                                            error: !!errors.pickupDate, helperText: errors.pickupDate
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>
                      
<Box>
  <CompactTextField
    fullWidth size="small"
    type="time"
    label="Pickup Time (Optional)"
    name="pickupTime"
    value={formData.pickupTime}
    onChange={(e) =>
      setFormData({
        ...formData,
        pickupTime: e.target.value, 
      })
    }

    
  />
</Box>

<Box>
  <Autocomplete
    freeSolo
    size="small"
    options={originSuggestions}
    inputValue={formData.origin || ""} 
    getOptionLabel={(option) => {
      if (typeof option === "string") return option;
      const city = option.text || "";
      const state = option.context?.find((c) => c.id.startsWith("region"));
      const stateShort =
        state?.short_code?.split("-")[1]?.toUpperCase() || "";
      return city && stateShort ? `${city}, ${stateShort}` : city || stateShort;
    }}
    onInputChange={async (event, newInputValue) => {
      setFormData((prev) => ({ ...prev, origin: newInputValue }));
      const suggestions = await getSuggestions(newInputValue);
      setOriginSuggestions(suggestions);
    }}
    onChange={async (event, selectedOption) => {
      if (!selectedOption) return;

      let city = "";
      let stateShort = "";

      try {
       
        if (
          typeof selectedOption === "object" &&
          selectedOption.place_type?.includes("postcode")
        ) {
          const zipCode = selectedOption.text;

          const res = await geocodingClient
            .forwardGeocode({
              query: zipCode,
              limit: 1,
              countries: ["US"],
            })
            .send();

          const feature = res.body.features?.[0];
          if (feature) {
            const placeCtx = feature.context?.find((c) =>
              c.id.startsWith("place")
            );
            const regionCtx = feature.context?.find((c) =>
              c.id.startsWith("region")
            );

            city =
              placeCtx?.text ||
              feature.text ||
              feature.properties?.context?.place?.name ||
              "";
            stateShort =
              regionCtx?.short_code?.split("-")[1]?.toUpperCase() || "";
          }
        } else if (typeof selectedOption === "object") {
         
          const regionCtx = selectedOption.context?.find((c) =>
            c.id.startsWith("region")
          );
          const placeCtx = selectedOption.context?.find((c) =>
            c.id.startsWith("place")
          );

          city = selectedOption.text || placeCtx?.text || "";
          stateShort =
            regionCtx?.short_code?.split("-")[1]?.toUpperCase() || "";
        } else {
          city = selectedOption;
        }

        const formatted =
          city && stateShort ? `${city}, ${stateShort}` : city || stateShort;

        setFormData((prev) => ({ ...prev, origin: formatted }));

        await validateAndFormatLocation(
          formatted,
          setFormData,
          setErrors,
          "origin",
          (val) => setFormData((prev) => ({ ...prev, origin: val }))
        );
      } catch (err) {
        console.error("Origin ZIP conversion error:", err);
      }
    }}
    renderInput={(params) => (
      <CompactTextField
        {...params}
        fullWidth
        label="Origin"
        required
        name="origin"
        error={!!errors.origin}
        helperText={errors.origin}
        inputRef={originInputRef}
        onBlur={async () => {
          if (formData.origin) {
            setValidatingOrigin(true);
            await validateAndFormatLocation(
              formData.origin,
              setFormData,
              setErrors,
              "origin",
             (val) => setFormData((prev) => ({ ...prev, origin: val })),
              "originCoords"
            );
            setValidatingOrigin(false);
          }
        }}
      />
    )}
  />
</Box>

<Box>
  <Autocomplete
    freeSolo
    size="small"
    options={destinationSuggestions}
    inputValue={formData.destination || ""}
    getOptionLabel={(option) => {
      if (typeof option === "string") return option;
      const city = option.text || "";
      const state = option.context?.find((c) => c.id.startsWith("region"));
      const stateShort =
        state?.short_code?.split("-")[1]?.toUpperCase() || "";
      return city && stateShort ? `${city}, ${stateShort}` : city || stateShort;
    }}
    onInputChange={async (event, newInputValue) => {
      setFormData((prev) => ({ ...prev, destination: newInputValue }));
      const suggestions = await getSuggestions(newInputValue);
      setDestinationSuggestions(suggestions);
    }}
    onChange={async (event, selectedOption) => {
      if (!selectedOption) return;

      let city = "";
      let stateShort = "";

      try {
        
        if (
          typeof selectedOption === "object" &&
          selectedOption.place_type?.includes("postcode")
        ) {
          const zipCode = selectedOption.text;

          const res = await geocodingClient
            .forwardGeocode({
              query: zipCode,
              limit: 1,
              countries: ["US"],
            })
            .send();

          const feature = res.body.features?.[0];
          if (feature) {
            const placeCtx = feature.context?.find((c) =>
              c.id.startsWith("place")
            );
            const regionCtx = feature.context?.find((c) =>
              c.id.startsWith("region")
            );

            city =
              placeCtx?.text ||
              feature.text ||
              feature.properties?.context?.place?.name ||
              "";
            stateShort =
              regionCtx?.short_code?.split("-")[1]?.toUpperCase() || "";
          }
        } else if (typeof selectedOption === "object") {
       
          const regionCtx = selectedOption.context?.find((c) =>
            c.id.startsWith("region")
          );
          const placeCtx = selectedOption.context?.find((c) =>
            c.id.startsWith("place")
          );

          city = selectedOption.text || placeCtx?.text || "";
          stateShort =
            regionCtx?.short_code?.split("-")[1]?.toUpperCase() || "";
        } else {
          city = selectedOption;
        }

        const formatted =
          city && stateShort ? `${city}, ${stateShort}` : city || stateShort;

        setFormData((prev) => ({ ...prev, destination: formatted }));

   
        await validateAndFormatLocation(
          formatted,
          setFormData,
          setErrors,
          "destination",
         (val) => setFormData((prev) => ({ ...prev, destination: val })),
          "destinationCoords"
        );
      } catch (err) {
        console.error("Destination ZIP conversion error:", err);
      }
    }}
    renderInput={(params) => (
      <CompactTextField
        {...params}
        fullWidth
        size="small"
        label="Destination"
        required
        name="destination"
        error={!!errors.destination}
        helperText={errors.destination}
        inputRef={destinationInputRef}
        onBlur={async () => {
          if (formData.destination) {
            setValidatingDestination(true);
            await validateAndFormatLocation(
              formData.destination,
              setFormData,
              setErrors,
              "destination",
              (val) =>
                setFormData((prev) => ({ ...prev, destination: val })),
              "destinationCoords"
            );
            setValidatingDestination(false);
          }
        }}
      />
    )}
  />
</Box>

<Box>
  <CompactTextField
    fullWidth size="small"
    type="time"
    label="Dropoff Time (Optional)"
    name="dockTime"
    value={formData.dockTime}
    onChange={(e) =>
      setFormData({
        ...formData,
        dockTime: e.target.value, 
      })
    }
  />
</Box>
                        <Box>
                            <CompactFormControl required fullWidth variant="outlined" size="small" error={!!errors.equipment}>
                                <InputLabel>Equipment Type</InputLabel>
<CompactSelect
  required 
  name="equipment" 
  value={formData.equipment} 
  onChange={handleChange} 
  label="Equipment Type "
>
  {equipmentTypes.map(type => (
    <MenuItem key={type.id} value={type.label}>
      {type.label}
    </MenuItem>
  ))}
</CompactSelect>

                                {errors.equipment && <Typography variant="caption" color="error" sx={{ ml: 1, mt: 0.5, display: 'block' }}>{errors.equipment}</Typography>}
                            </CompactFormControl>
                        </Box>
                       
                        <Box>
                            <CompactFormControl required fullWidth variant="outlined" size="small" error={!!errors.fullOrPartial}>
                                <InputLabel>Load Type</InputLabel>
                                <CompactSelect name="fullOrPartial" value={formData.fullOrPartial} onChange={handleChange} label="Load Type *">
                                    <MenuItem value="Full">Full</MenuItem>
                                    <MenuItem value="Partial">Partial</MenuItem>
                                </CompactSelect>
                                {errors.fullOrPartial && <Typography variant="caption" color="error" sx={{ ml: 1, mt: 0.5, display: 'block' }}>{errors.fullOrPartial}</Typography>}
                            </CompactFormControl>
                        </Box>
                       
<Box>
    <CompactTextField
        required size="small"
        fullWidth label="Feet" name="length" type="number"
        value={formData.length} onChange={handleChange}
        error={!!errors.length} helperText={errors.length}
        InputProps={{ 
            endAdornment: (<InputAdornment position="end">ft</InputAdornment>), 
        }}
        
        inputProps={{ min: 0, max: 999 }} 
    />
</Box>

<Box>
    <CompactTextField
        required size="small"
        fullWidth label="Weight" name="weight" type="number"
        value={formData.weight} onChange={handleChange}
        error={!!errors.weight} helperText={errors.weight}
        InputProps={{ 
            endAdornment: (<InputAdornment position="end">lbs</InputAdornment>), 
        }}
        
        inputProps={{ min: 0, max: 99999999 }} 
    />
</Box>
                        <Box>
                           <CompactTextField
    fullWidth size="small" label="Commodity (Optional)" name="commodity"
    value={formData.commodity} onChange={handleChange}
    error={!!errors.commodity} helperText={errors.commodity}
/>
                        </Box>
                       
                        <Box>
                            <CompactTextField
                                fullWidth size="small" label="Offer Rate" name="offerRate" type="number"
                                value={formData.offerRate} onChange={handleChange}
                                error={!!errors.offerRate} helperText={errors.offerRate}
                                inputProps={{ min: 0, max: 999999.99, step: 0.01 }}
                                InputProps={{ startAdornment: (<InputAdornment position="start">$</InputAdornment>), }}
                            />
                        </Box>
                      
                        <Box>
                            <CompactFormControl fullWidth variant="outlined" size="small" error={!!errors.rateType}>
                                <InputLabel>Rate Type</InputLabel>
                                <CompactSelect name="rateType" value={formData.rateType} onChange={handleChange} label="Rate Type *">
                                    <MenuItem value="Flat">Flat</MenuItem>
                                    <MenuItem value="Per Mile">Per Mile</MenuItem>
                                </CompactSelect>
                                {errors.rateType && <Typography variant="caption" color="error" sx={{ ml: 1, mt: 0.5, display: 'block' }}>{errors.rateType}</Typography>}
                            </CompactFormControl>
                        </Box>
                        
                        <Box>
                            <CompactTextField
    fullWidth size="small"
    label="Trip Miles"
    name="tripMiles"
    type="number"
    value={formData.tripMiles || ""}
    disabled
    InputProps={{ endAdornment: (<InputAdornment position="end">mi</InputAdornment>) }}
/>

                        </Box>
                      
<Box>
  <CompactTextField
    select 
    fullWidth size="small"
    label="Contact Method"
    name="contactMethod"
    
    value={formData.contactMethod || ''} 
    onChange={handleChange}
    disabled={!currentUser} 
  >
    
    
    {currentUser?.phone_number && (
      <MenuItem value={currentUser.phone_number}>
       
        {formatPhoneNumber(currentUser.phone_number)} 
      </MenuItem>
    )}
    
    {currentUser?.email && (
      <MenuItem value={currentUser.email}>
        {currentUser.email} 
      </MenuItem>
    )}
    
    
    {(!currentUser?.phone_number && !currentUser?.email) && (
      <MenuItem value="" disabled>
        No Contact Methods Found
      </MenuItem>
    )}

  </CompactTextField>
</Box>
                      
                        <Box>
                            <CompactTextField
                                fullWidth  size="small" label="Reference ID (Optional)" name="referenceId"
                                value={formData.referenceId} onChange={handleChange}
                                error={!!errors.referenceId} helperText={errors.referenceId}
                            />
                        </Box>
                      
                       {!isBroker && (
    <>
        <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2', md: 'span 2' } }}>
            <CompactFormControl required fullWidth variant="outlined" size="small">
                <InputLabel>Payment Terms</InputLabel>
                <CompactSelect
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleChange}
                    label="Payment Terms *"
                > 
                    {paymentMethods.map(method => (
                        <MenuItem key={method.id} value={method.label}>
                            {method.label}
                        </MenuItem>
                    ))}
                </CompactSelect>
            </CompactFormControl>
        </Box>
 {formData.paymentMethod === 'Custom Payment Terms' && (
            <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}>
                <CompactTextField
                    required size="small" fullWidth label="Custom Payment Terms Details" name="customTermsDetails"
                    value={formData.customTermsDetails} onChange={handleChange}
                    error={!!errors.customTermsDetails} helperText={errors.customTermsDetails}
                />
            </Box>
        )}
    </>
)}

        {/* Comment field - sits next to Payment Method */}
        <Box>
            <TextField
                size='small'
                fullWidth
                multiline
                minRows={1} 
                maxRows={3}
                label="Comment (Optional)"
                name="comment"
                value={formData.comment}
                onChange={handleChange}
                sx={{
                    '& .MuiInputBase-root': { fontSize: '0.85rem' },
                    '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                }}
            />
        </Box>

       
        {/* Buttons row */}
        <Box sx={{ 
            gridColumn: '1 / -1',
            display: 'flex',
            gap: 1.5,
            justifyContent: 'flex-end',
            mt: 0.5,
            flexWrap: 'wrap',
            alignItems: 'center'
        }}>
            <Tooltip title="Collapse Form">
                <IconButton size="small" onClick={() => setFormOpen(false)} sx={{ color: 'primary.main' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <KeyboardArrowUpIcon sx={{ fontSize: '1.2rem', color: 'grey.500' }} />
                        <KeyboardArrowUpIcon sx={{ fontSize: '1.2rem', mt: -1.5, color: 'grey.500' }} />
                        <KeyboardArrowUpIcon sx={{ fontSize: '1.2rem',mt: -1.5, }} />
                    </Box>
                </IconButton>
            </Tooltip>
        
            <ClearButton 
                variant="outlined" 
                onClick={handleClear} 
                startIcon={<ClearIcon />} 
                size="small"
            >
                Clear
            </ClearButton>
            <PostButton 
                variant="contained" 
                onClick={handleSubmit} 
                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <PostAddIcon />} 
                size="small"
                disabled={isSubmitting}
            >
                {isSubmitting ? 'Posting...' : 'Post Load'}
            </PostButton>
        </Box>
                    </Box>
                    </Collapse>
                </StyledPaper>

              
                {postedLoads.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', mb: 1, fontWeight: 600 }}>
                           {`My Posted Loads (`}
{postedLoads.filter(load => {
    const today = dayjs().startOf('day');
    const pickupDate = load.pickupDate ? dayjs(load.pickupDate).startOf('day') : null;
    return pickupDate && (pickupDate.isSame(today, 'day') || pickupDate.isAfter(today));
}).length}
{`)`}
                        </Typography>
                        <TableContainer 
                        component={Paper} sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                
                                <TableHead sx={{ backgroundColor: theme.palette.primary.main, position: 'sticky', top: 0, zIndex: 10 }}>
                                    <TableRow>
                                        {["", "Posted", "Date", "Origin", "Destination", "Equipment", "Rate","Status", "Actions"].map((col, index) => (
                                            <TableCell key={index} sx={{ 
                    color: 'white', 
                    
                    fontWeight: 600, 
                    fontSize: '0.75rem', 
                    py: 0.5, 
                    textAlign: 'center',
                    borderBottom: 'none'
                }}>
                    {col}
                    {index === 0 && <TableProgressBar sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', zIndex: 5 }} />}
                </TableCell>
                                            
                                        ))}
                                    </TableRow>
                                </TableHead>
                                
                                <TableBody>
                                                                        {postedLoads
                                                                           .filter(load => {
    const today = dayjs().startOf('day');
    const pickupDate = load.pickupDate ? dayjs(load.pickupDate).startOf('day') : null;
    return pickupDate && (pickupDate.isSame(today, 'day') || pickupDate.isAfter(today));
})
                                                                            .map((load, idx) => (
                                                                                <LoadRow
                                                                                        key={load.id}
                                                                                        load={load}
                                                                                        idx={idx}
                                                                                        handleEdit={handleEditWithConfirm}
                                                                                        handleDelete={handleDeleteWithConfirm}
                                                                                        handleRepost={handleRepostWithConfirm}
                                                                                        handleToggleStatus={handleToggleStatusWithConfirm}
                                                                                        repostCooldowns={repostCooldowns}
                                                                                        currentUser={currentUser}
                                                                                        repostingIds={repostingIds}
                                                                                />
                                                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
               
                {postedLoads.length === 0 && (
                    <Box sx={{ textAlign: 'center', mt: 4, mb: 4 }}>
                        <PostAddIcon sx={{ fontSize: 80, color: theme.palette.primary.main, mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                            Post your loads to see them appear here and find matching trucks.
                        </Typography>
                    </Box>
                )}

             
                <Snackbar
                    open={submissionStatus.open}
                    autoHideDuration={5000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={submissionStatus.severity}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {submissionStatus.message}
                    </Alert>
                </Snackbar>
            </Container>
        </ThemeProvider>
    );
};

export default PostLoads;
       